import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import BaseFormComponent, { type RequiredIndicator, useFormFieldIds } from '../BaseFormComponent';
import { Button } from '../Button';
import CopyIcon from '../CustomIcons/CopyIcon';
import InfoIcon from '../CustomIcons/InfoIcon';
import FieldMessage from '../FieldMessage';
import { paintedFieldValue } from '../devWarning';
import type { ButtonDefinition, TextAreaSize } from './types';

/**
 * `assistant` is the chrome for a field sitting in an AI context (e.g. `Chat.Composer`'s
 * policy-assistant tone): the field's own focus border recolours to the assistant violet
 * instead of the default accent — same indicator, different colour, not an extra one. Same
 * naming precedent as `PillButton`'s own `tone`.
 */
type TextAreaTone = 'default' | 'assistant';

interface TextAreaProps {
    label?: string;
    hint?: string;
    tooltip?: string;
    placeholder?: string;
    icon?: React.ReactNode;
    readOnly?: boolean;
    name: FieldPath<FieldValues> | string;
    description?: string;
    size?: TextAreaSize;
    required?: boolean;
    requiredIndicator?: RequiredIndicator;
    error?: boolean;
    loading?: boolean;
    defaultValue?: string;
    disabled?: boolean;
    prefixValue?: string;
    prefixSecondaryValue?: string;
    ellipsis?: boolean;
    formControl?: Control<FieldValues, unknown>;
    buttons?: ButtonDefinition[];
    onCopyClick?: () => void;
    onChange?: (value: string) => void;
    value?: string;
    onBlur?: () => void;
    /** Key handling the control cannot own, e.g. Enter-to-submit in a chat composer. */
    onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    /**
     * A control that belongs inside the field, pinned bottom-right — a chat composer's send.
     * The field reserves room for it, so text cannot run underneath.
     */
    inFieldAction?: React.ReactNode;
    /** See `TextAreaTone`. Defaults to `'default'`, so no existing field changes. */
    tone?: TextAreaTone;
    helperText?: string;
    rows?: number;
    /**
     * Grows the field from `rows` up to this many lines as content is typed, then scrolls
     * internally rather than growing further. Omitted keeps the field at a fixed `rows`
     * height, the pre-existing behaviour.
     */
    maxRows?: number;
    maxLength?: number;
    resize?: 'none' | 'vertical' | 'horizontal' | 'both';
    /**
     * When false, lines are not soft-wrapped — long content scrolls horizontally
     * instead of wrapping (native `wrap="off"`). Useful for one-value-per-line
     * content like URLs. Defaults to true.
     */
    wrap?: boolean;
    id?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
}

const getSizeClasses = (size: TextAreaSize): string => {
    switch (size) {
        case 'small':
            return 'text-sm';
        case 'medium':
            return 'text-[length:var(--malachite-font-size-15,15px)] leading-[1.4]';
        case 'large':
            return 'text-base';
        default:
            return 'text-[length:var(--malachite-font-size-15,15px)] leading-[1.4]';
    }
};

/**
 * A rejection always wins the border colour, regardless of `tone`: a field the caller has
 * flagged invalid stays destructive-red on focus even inside an assistant surface.
 *
 * Each branch spells its two classes out in full rather than interpolating a shared colour
 * variable into them. Tailwind extracts candidates by scanning source text, so a class built
 * at runtime is one Tailwind never sees — and the CSS is simply absent, with no build error.
 * The assistant pair was surviving only because the same literal appears in a spec's
 * assertion, which the content glob also scans: rewording that assertion would have silently
 * dropped the violet focus border.
 */
const focusBorderClasses = (isError: boolean, tone: TextAreaTone): string => {
    if (isError) {
        return 'focus-within:border-[var(--malachite-destructive-border-hover)] hover:border-[var(--malachite-destructive-border-hover)]';
    }

    if (tone === 'assistant') {
        return 'focus-within:border-[var(--malachite-assistant-foreground)] hover:border-[var(--malachite-assistant-foreground)]';
    }

    return 'focus-within:border-[var(--malachite-control-border-hover)] hover:border-[var(--malachite-control-border-hover)]';
};

// Shared by the static min-height and the auto-grow ceiling, so a field's grown height
// lines up with the same row metrics its resting height already used.
const rowHeightPx = (size: TextAreaSize, rows: number): number => {
    const baseHeight = {
        small: 20,
        medium: 22,
        large: 24,
    }[size];

    // Every size renders `py-[12px]`, so the vertical padding is 24 regardless of size.
    const padding = 24;

    return baseHeight * rows + padding;
};

const getMinHeight = (size: TextAreaSize, rows: number): string => `${rowHeightPx(size, rows)}px`;

const TextArea: React.FC<TextAreaProps> = ({
    label,
    hint,
    tooltip,
    placeholder,
    icon,
    name,
    required,
    requiredIndicator,
    error,
    description,
    loading = false,
    readOnly = false,
    size = 'medium',
    defaultValue,
    disabled,
    prefixValue,
    prefixSecondaryValue,
    // ellipsis = false,
    formControl,
    buttons,
    onCopyClick,
    onChange,
    onKeyDown,
    inFieldAction,
    tone = 'default',
    value,
    onBlur,
    helperText,
    rows = 4,
    maxRows,
    maxLength,
    resize = 'vertical',
    wrap = true,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
}) => {
    const { controlId, labelId, descriptionId, hintId, helperTextId } = useFormFieldIds({
        id,
        label,
        description,
        hint,
        helperText: true,
    });
    const [internalValue, setInternalValue] = useState(defaultValue || '');
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Reset-then-measure rather than growing incrementally: `scrollHeight` on a field already
    // taller than its content stays stuck at that height, so the reset is what lets a paste
    // followed by a delete shrink the field back down, not just grow it. Mutates the DOM
    // directly instead of round-tripping through React state, so typing a character never
    // re-renders this component just to change its own height.
    const resizeToFit = useCallback(() => {
        const el = textareaRef.current;
        if (!el || maxRows === undefined) {
            return;
        }

        const maxHeight = rowHeightPx(size, maxRows);
        el.style.height = 'auto';
        const nextHeight = Math.min(el.scrollHeight, maxHeight);
        el.style.height = `${nextHeight}px`;
        el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [maxRows, size]);

    // Catches every change `onChange` on the element itself would miss: the initial mount,
    // and a value the parent sets from outside (e.g. clearing the composer after send).
    // biome-ignore lint/correctness/useExhaustiveDependencies: `value` is not read in the body, but a controlled `value` changing from outside is exactly the case `onChange` cannot see, so the effect has to re-run on it anyway.
    useLayoutEffect(() => {
        resizeToFit();
    }, [resizeToFit, value]);

    const isControlled = value !== undefined;
    const paintedValue = (formValue: unknown): string =>
        paintedFieldValue({
            component: 'TextArea',
            name,
            value,
            formValue,
            defaultValue,
            bound: !!formControl,
            uncontrolledValue: internalValue,
        });

    const handleChange =
        (reportToForm?: (value: unknown) => void) =>
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newValue = e.target.value;
            if (!isControlled) {
                setInternalValue(newValue);
            }
            // The browser has already applied the keystroke to the element by the time this
            // fires, so `scrollHeight` already reflects it — no need to wait for a re-render.
            resizeToFit();
            onChange?.(newValue);
            reportToForm?.(newValue);
        };

    const hasPrefix = icon || prefixValue || prefixSecondaryValue;

    const errorMessageId = `${helperTextId}-error`;

    const renderTextArea = (
        isError: boolean,
        resolvedHelperText?: string,
        onFieldChange?: (value: unknown) => void,
        onFieldBlur?: () => void,
        errorMessage?: string,
        formValue?: unknown,
    ) => {
        const textAreaValue = paintedValue(formValue);
        // The icon is the only signal a consumer-forced `error` has; beside a message it is
        // duplication, and it crowds the suffix next to a reveal or copy control.
        const showErrorIcon = isError && !errorMessage;
        const hasSuffix =
            showErrorIcon || (onCopyClick && disabled && textAreaValue) || buttons?.length;
        const describedBy =
            [
                descriptionId,
                hintId,
                resolvedHelperText ? helperTextId : undefined,
                errorMessage ? errorMessageId : undefined,
            ]
                .filter(Boolean)
                .join(' ') || undefined;
        const wrapperClasses = [
            'relative',
            'flex',
            'w-full',
            'rounded-xl',
            'border border-solid',
            disabled
                ? 'bg-[var(--malachite-card-dimmed-background)]'
                : 'bg-[var(--malachite-card-background)]',
            'transition-colors duration-200 ease-in-out',
            isError ? 'shadow-error' : 'shadow-default',
            isError
                ? 'border-[var(--malachite-destructive-border)]'
                : 'border-[var(--malachite-card-border)]',
            // One border-hover colour, not two overlapping utilities for the same states: a
            // duplicate pair used to sit above this one, fixed at the default accent — since
            // it targeted the identical `focus-within`/`hover` selectors, a `tone` swap here
            // would otherwise leave a second, conflicting colour in the class list.
            //
            // No separate ring on top of it: the recoloured border IS this field's focus
            // indicator, the same as the default tone's. Adding a ring here drew a second,
            // concentric outline around the first on `assistant` — the field already has one,
            // it only needed to change colour.
            disabled ? '' : focusBorderClasses(isError, tone),
        ]
            .filter(Boolean)
            .join(' ');

        const textAreaClasses = [
            'flex-1',
            'bg-transparent',
            'border-none',
            'outline-none',
            'text-[var(--malachite-text-primary)]',
            'placeholder:text-[var(--malachite-text-placeholder)]',
            'disabled:text-[var(--malachite-text-dimmed)]',
            'disabled:cursor-not-allowed',
            getSizeClasses(size),
            // Emitted as one side each when an action sits in the field: `px` and `pr` are
            // the same specificity, so which wins would rest on Tailwind's output order.
            inFieldAction ? 'pl-[16px] pr-[52px]' : 'px-[16px]',
            'py-[12px]',
            resize === 'none'
                ? 'resize-none'
                : resize === 'vertical'
                  ? 'resize-y'
                  : resize === 'horizontal'
                    ? 'resize-x'
                    : 'resize',
            wrap ? '' : 'whitespace-pre overflow-x-auto min-w-0',
        ]
            .filter(Boolean)
            .join(' ');

        const textAreaElement = (
            <textarea
                ref={textareaRef}
                id={controlId}
                placeholder={placeholder}
                value={textAreaValue}
                onChange={handleChange(onFieldChange)}
                onBlur={() => {
                    onBlur?.();
                    onFieldBlur?.();
                }}
                onKeyDown={onKeyDown}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                aria-invalid={isError || undefined}
                aria-required={required || undefined}
                aria-describedby={describedBy}
                disabled={disabled || !!prefixValue || !!prefixSecondaryValue || readOnly}
                readOnly={readOnly}
                wrap={wrap ? undefined : 'off'}
                rows={rows}
                maxLength={maxLength}
                style={{ minHeight: getMinHeight(size, rows) }}
                className={textAreaClasses}
                name={name}
            />
        );

        return (
            <div className={wrapperClasses}>
                {hasPrefix && (
                    <div className="flex pt-3 pl-3 pr-1 flex-shrink-0 pointer-events-none">
                        <CompositeIcon
                            prefixValue={prefixValue}
                            prefixSecondaryValue={prefixSecondaryValue}
                            icon={icon}
                        />
                    </div>
                )}

                {textAreaElement}

                {inFieldAction && (
                    <div className="absolute bottom-2 right-2 flex items-center">
                        {inFieldAction}
                    </div>
                )}

                {hasSuffix && (
                    <div className="flex pt-3 pl-1 pr-3 flex-shrink-0">
                        <Suffix
                            error={showErrorIcon}
                            disabled={disabled}
                            defaultValue={textAreaValue}
                            onCopyClick={onCopyClick}
                        />
                        <TextAreaButtons buttons={buttons} />
                    </div>
                )}
            </div>
        );
    };

    const renderHelperText = () =>
        helperText ? (
            <div
                id={helperTextId}
                className="text-[length:var(--malachite-font-size-small,13px)] text-left pl-2 text-[var(--malachite-text-dimmed,rgb(161,161,161))]"
            >
                {helperText}
            </div>
        ) : null;

    const renderErrorMessage = (errorMessage?: string) => (
        <FieldMessage id={errorMessageId} message={errorMessage} spaced />
    );

    return (
        <BaseFormComponent
            label={label}
            hint={hint}
            tooltip={tooltip}
            name={name}
            required={required}
            requiredIndicator={requiredIndicator}
            loading={loading}
            formControl={formControl}
            description={description}
            controlId={controlId}
            labelId={labelId}
            descriptionId={descriptionId}
            hintId={hintId}
            loadingComponent={<LoadingTextArea size={size} rows={rows} />}
        >
            {({ error: fieldError, message, onFieldChange, onFieldBlur, value: formValue }) => {
                const isError = error ?? fieldError;
                // `error={false}` opts out of the resolver entirely, message included: a
                // screen that has dealt with the rejection its own way would otherwise keep
                // reporting it in destructive text and through `aria-describedby`.

                // The message is its own line rather than a replacement for the helper text:
                // a field that explains what it wants still has to say why what was typed was
                // rejected, and dropping one for the other left the field marked red with
                // nothing anywhere saying what was wrong.
                const errorMessage = error === false ? undefined : message;

                return (
                    <>
                        {renderTextArea(
                            isError,
                            helperText,
                            onFieldChange,
                            onFieldBlur,
                            errorMessage,
                            formValue,
                        )}
                        {renderHelperText()}
                        {renderErrorMessage(errorMessage)}
                    </>
                );
            }}
        </BaseFormComponent>
    );
};

const LoadingTextArea: React.FC<{ size?: TextAreaSize; rows?: number }> = ({
    size = 'medium',
    rows = 4,
}) => {
    return (
        // Decorative height placeholder — kept out of the a11y tree so it is not announced
        // as an unlabelled box (same as Skeleton and LoadingInput).
        <div
            aria-busy="true"
            aria-hidden="true"
            className="w-full rounded-xl border border-solid border-[var(--malachite-card-border)] bg-[var(--malachite-card-border)] flex items-center justify-center shadow-[var(--malachite-shadow-default)]"
            style={{ minHeight: getMinHeight(size, rows) }}
        />
    );
};

const CompositeIcon: React.FC<{
    prefixValue?: string;
    prefixSecondaryValue?: string;
    icon?: React.ReactNode;
}> = ({ prefixValue, prefixSecondaryValue, icon }) => {
    return (
        <div className="flex items-start gap-1">
            {icon && (
                <span className="flex items-center justify-center text-[var(--malachite-text-dimmed)] w-4 h-4 flex-shrink-0 mt-1">
                    {icon}
                </span>
            )}
            {prefixValue && (
                <span className="text-[length:var(--malachite-font-size-15,15px)] leading-[1.4] text-[var(--malachite-text-dimmed)] whitespace-nowrap">
                    {prefixValue}
                </span>
            )}
            {prefixSecondaryValue && (
                <span className="text-[length:var(--malachite-font-size-15,15px)] leading-[1.4] text-[var(--malachite-text-dimmed)] whitespace-nowrap">
                    {prefixSecondaryValue}
                </span>
            )}
        </div>
    );
};

const Suffix: React.FC<{
    error?: boolean;
    disabled?: boolean;
    defaultValue?: string;
    onCopyClick?: () => void;
}> = ({ error, disabled, defaultValue, onCopyClick }) => {
    return (
        <div className="flex items-start gap-1">
            {error && (
                // Decorative: the rejection is carried by the message beneath the field.
                <div
                    aria-hidden="true"
                    className="flex items-center justify-center w-5 h-5 flex-shrink-0 mt-1"
                >
                    <InfoIcon
                        size={16}
                        className="text-[var(--malachite-destructive-foreground,rgb(196,62,62))]"
                    />
                </div>
            )}
            {onCopyClick && disabled && defaultValue && (
                <button
                    onClick={() => {
                        navigator.clipboard.writeText(defaultValue);
                        onCopyClick();
                    }}
                    type="button"
                    aria-label="Copy to clipboard"
                    className="flex items-center justify-center w-5 h-5 hover:bg-[var(--malachite-primary-border)] rounded transition-colors flex-shrink-0 mt-1"
                >
                    <CopyIcon size={16} />
                </button>
            )}
        </div>
    );
};

const TextAreaButtons: React.FC<{
    buttons?: ButtonDefinition[];
}> = ({ buttons }) => {
    if (!buttons?.length) {
        return null;
    }

    return (
        <div className="flex flex-col gap-1 mt-1">
            {buttons.map((button) => (
                <Button
                    key={button.label}
                    variant={button.danger ? 'destructive' : 'outline'}
                    size="small"
                    disabled={button.disabled}
                    icon={button.icon}
                    tooltip={button.tooltip}
                    onClick={button.onClick}
                >
                    {button.label}
                </Button>
            ))}
        </div>
    );
};

export default TextArea;
